import { useNavigate } from "@tanstack/react-router";
import Chip from "@mui/material/Chip";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

import { currencyFormatter } from "../../utils/Utils";
import { DataTable } from "../../components/DataTable";

const columns = [
  { accessorKey: "documentNo", header: "No.", meta: { minWidth: 200 } },
  { accessorKey: "documentType", header: "Type", meta: { minWidth: 150 } },
  {
    accessorKey: "postingDate",
    header: "Invoice Date",
    meta: { minWidth: 150 },
  },
  { accessorKey: "dueDate", header: "Due Date", meta: { minWidth: 150 } },
  {
    accessorKey: "overdue",
    header: "Status",
    meta: { minWidth: 150 },
    cell: (info) => {
      const dueDate = dayjs(info.row.original.dueDate, "DD/MM/YY");
      const daysOutstanding = dayjs().diff(dueDate, "day");
      const overdue = info.getValue() === "True";
      const days = Math.abs(daysOutstanding);

      return (
        <Chip
          variant="outlined"
          color={overdue ? "error" : "info"}
          clickable={false}
          label={
            overdue
              ? `${daysOutstanding} ${daysOutstanding > 1 ? "days" : "day"} overdue`
              : `Due in ${days} ${days > 1 ? "days" : "day"}`
          }
        />
      );
    },
  },
  {
    accessorKey: "description",
    header: "Related Document",
    meta: { minWidth: 150 },
  },
  {
    id: "amount",
    accessorFn: (row) => currencyFormatter(row.currencyCode, row.amount),
    header: "Amount",
    meta: { minWidth: 150 },
  },
  {
    id: "remainingAmount",
    accessorFn: (row) =>
      currencyFormatter(row.currencyCode, row.remainingAmount),
    header: "Outstanding Amount",
    meta: { minWidth: 150 },
  },
];

export const AccountBalanceTable = ({ data, loading, filter, setFilter }) => {
  const navigate = useNavigate();

  const accountBalance = data?.ledger || [];
  const rowCount = Number(data?.paging?.noOfRecords) || 0;

  const pagination = {
    page: (filter?.page ?? 1) - 1,
    pageSize: filter?.pageSize ?? 25,
  };

  const handlePaginationChange = (next) => {
    setFilter((prevState) => ({
      ...prevState,
      page: next.page + 1,
      pageSize: next.pageSize,
    }));
  };

  const handleRowClick = (row) => {
    const docType = row.documentType === "Invoice" ? "invoices" : "credit";
    navigate({ to: `/${docType}/${row.documentNo}` });
  };

  return (
    <DataTable
      aria-label="Account balance"
      columns={columns}
      rows={accountBalance}
      getRowId={(row) => row.documentNo}
      rowCount={rowCount}
      page={pagination.page}
      pageSize={pagination.pageSize}
      loading={loading}
      onPaginationChange={handlePaginationChange}
      onRowClick={handleRowClick}
    />
  );
};
